import React, { useState, useEffect, useRef, createContext, useContext } from 'react';

/**
 * Network Information Hook - Detects connection quality
 * Uses Navigator.connection API with fallbacks
 */
const useNetworkInfo = () => {
  const [networkInfo, setNetworkInfo] = useState({
    effectiveType: '4g',      // '4g', '3g', '2g', 'slow-2g'
    downlink: 10,             // Mbps
    saveData: false,          // Data saver mode
    quality: 'high',          // 'high', 'medium', 'low'
    isOnline: true
  });

  useEffect(() => {
    const updateNetworkInfo = () => {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const isOnline = navigator.onLine !== false;
      
      if (connection) {
        const effectiveType = connection.effectiveType || '4g';
        const downlink = connection.downlink || 10;
        const saveData = connection.saveData || false;
        
        // Determine quality based on connection
        let quality = 'high';
        if (saveData || effectiveType === 'slow-2g' || effectiveType === '2g') {
          quality = 'low';
        } else if (effectiveType === '3g' || downlink < 1.5) {
          quality = 'medium';
        }
        
        setNetworkInfo({
          effectiveType,
          downlink,
          saveData,
          quality,
          isOnline
        });
      } else {
        // Fallback: assume good connection but check online status
        setNetworkInfo(prev => ({ ...prev, isOnline }));
      }
    };

    updateNetworkInfo();

    // Listen for connection changes
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      connection.addEventListener('change', updateNetworkInfo);
    }
    
    window.addEventListener('online', updateNetworkInfo);
    window.addEventListener('offline', updateNetworkInfo);

    return () => {
      if (connection) {
        connection.removeEventListener('change', updateNetworkInfo);
      }
      window.removeEventListener('online', updateNetworkInfo);
      window.removeEventListener('offline', updateNetworkInfo);
    };
  }, []);

  return networkInfo;
};

/**
 * Network Context - Share network info across components
 */
const NetworkContext = createContext(null);

export const NetworkProvider = ({ children }) => {
  const networkInfo = useNetworkInfo();
  return (
    <NetworkContext.Provider value={networkInfo}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = () => {
  const context = useContext(NetworkContext);
  // If not wrapped in provider, use hook directly
  const fallbackInfo = useNetworkInfo();
  return context || fallbackInfo;
};

/**
 * LazyImage - Network-aware lazy loading image component
 * 
 * Props:
 * - src: High-quality image source URL (required)
 * - srcMedium: Medium-quality image URL (optional)
 * - srcLow: Low-quality/thumbnail image URL (optional)
 * - alt: Alt text for accessibility
 * - className: Additional CSS classes
 * - placeholderColor: Background color while loading (default: sage)
 * - blurHash: Optional blur hash or low-res placeholder URL
 * - aspectRatio: Aspect ratio for placeholder (e.g., "16/9", "1/1", "4/3")
 * - forceQuality: Override auto quality selection ('high', 'medium', 'low')
 * - showNetworkBadge: Show connection quality indicator (default: false)
 * - onQualityChange: Callback when quality changes (quality, src) => void
 */
const LazyImage = ({ 
  src, 
  srcMedium = null,
  srcLow = null,
  alt = '', 
  className = '', 
  placeholderColor = '#DCE7E3',
  blurHash = null,
  aspectRatio = null,
  style = {},
  forceQuality = null,
  showNetworkBadge = false,
  onQualityChange = null,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(null);
  const [appliedQuality, setAppliedQuality] = useState('high');
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  
  // Get network information
  const networkInfo = useNetwork();

  // Determine which image source to use based on network
  useEffect(() => {
    const quality = forceQuality || networkInfo.quality;
    let selectedSrc = src;
    
    if (quality === 'low' && srcLow) {
      selectedSrc = srcLow;
    } else if (quality === 'medium' && (srcMedium || srcLow)) {
      selectedSrc = srcMedium || srcLow;
    }
    
    setCurrentSrc(selectedSrc);
    setAppliedQuality(quality);
    
    if (onQualityChange) {
      onQualityChange(quality, selectedSrc);
    }
  }, [src, srcMedium, srcLow, networkInfo.quality, forceQuality, onQualityChange]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { 
        threshold: 0.1,
        rootMargin: networkInfo.quality === 'low' ? '50px' : '100px' // Smaller preload margin on slow connections
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [networkInfo.quality]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    // On error, try falling back to lower quality if available
    if (appliedQuality === 'high' && srcMedium) {
      setCurrentSrc(srcMedium);
      setAppliedQuality('medium');
    } else if (appliedQuality !== 'low' && srcLow) {
      setCurrentSrc(srcLow);
      setAppliedQuality('low');
    } else {
      setHasError(true);
      setIsLoaded(true);
    }
  };

  // Network quality badge colors
  const qualityColors = {
    high: { bg: '#4D9078', text: '#FFFFFF', label: '4G' },
    medium: { bg: '#C9A96E', text: '#2C1810', label: '3G' },
    low: { bg: '#B85C38', text: '#FFFFFF', label: '2G' }
  };

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{
        backgroundColor: placeholderColor,
        aspectRatio: aspectRatio || 'auto',
        ...style
      }}
      data-network-quality={appliedQuality}
    >
      {/* Blur placeholder / shimmer effect */}
      <div 
        className={`absolute inset-0 transition-opacity duration-500 ${isLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ 
          backgroundColor: placeholderColor,
          backgroundImage: blurHash ? `url(${blurHash})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: blurHash ? 'blur(20px)' : 'none',
          transform: 'scale(1.1)'
        }}
      >
        {/* Shimmer overlay */}
        {!blurHash && (
          <div 
            className="absolute inset-0 animate-shimmer"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
              backgroundSize: '200% 100%'
            }}
          />
        )}
      </div>

      {/* Actual image - only load when in view */}
      {isInView && !hasError && currentSrc && (
        <img
          ref={imgRef}
          src={currentSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          {...props}
        />
      )}

      {/* Error state */}
      {hasError && (
        <div 
          className="absolute inset-0 flex flex-col items-center justify-center gap-2"
          style={{ backgroundColor: placeholderColor }}
        >
          <svg 
            className="w-12 h-12 opacity-30" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            style={{ color: '#5D4E47' }}
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
          {!networkInfo.isOnline && (
            <span className="text-xs font-medium" style={{ color: '#5D4E47' }}>Offline</span>
          )}
        </div>
      )}

      {/* Network quality badge */}
      {showNetworkBadge && isLoaded && !hasError && (
        <div 
          className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-semibold shadow-sm transition-opacity duration-300"
          style={{ 
            backgroundColor: qualityColors[appliedQuality].bg,
            color: qualityColors[appliedQuality].text
          }}
          title={`Connection: ${networkInfo.effectiveType} | Quality: ${appliedQuality}`}
        >
          {qualityColors[appliedQuality].label}
        </div>
      )}

      {/* Data saver indicator */}
      {networkInfo.saveData && showNetworkBadge && (
        <div 
          className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-semibold shadow-sm"
          style={{ backgroundColor: '#5D4E47', color: '#FFFFFF' }}
          title="Data Saver Mode Active"
        >
          <svg className="w-3 h-3 inline-block mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          Saver
        </div>
      )}
    </div>
  );
};

/**
 * LazyBackgroundImage - Network-aware lazy loading background image
 */
export const LazyBackgroundImage = ({ 
  src,
  srcMedium = null,
  srcLow = null,
  children, 
  className = '', 
  placeholderColor = '#DCE7E3',
  overlayOpacity = 0,
  style = {},
  forceQuality = null
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(null);
  const containerRef = useRef(null);
  
  const networkInfo = useNetwork();

  // Determine which image source to use based on network
  useEffect(() => {
    const quality = forceQuality || networkInfo.quality;
    let selectedSrc = src;
    
    if (quality === 'low' && srcLow) {
      selectedSrc = srcLow;
    } else if (quality === 'medium' && (srcMedium || srcLow)) {
      selectedSrc = srcMedium || srcLow;
    }
    
    setCurrentSrc(selectedSrc);
  }, [src, srcMedium, srcLow, networkInfo.quality, forceQuality]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1, rootMargin: networkInfo.quality === 'low' ? '50px' : '100px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [networkInfo.quality]);

  useEffect(() => {
    if (isInView && currentSrc) {
      const img = new Image();
      img.src = currentSrc;
      img.onload = () => setIsLoaded(true);
    }
  }, [isInView, currentSrc]);

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{ backgroundColor: placeholderColor, ...style }}
    >
      {/* Background image layer */}
      <div 
        className={`absolute inset-0 bg-cover bg-center transition-all duration-700 ${
          isLoaded ? 'opacity-100 blur-0 scale-100' : 'opacity-0 blur-lg scale-105'
        }`}
        style={{ backgroundImage: isInView && currentSrc ? `url(${currentSrc})` : 'none' }}
      />
      
      {/* Optional overlay */}
      {overlayOpacity > 0 && (
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
        />
      )}
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

/**
 * NetworkStatus - Display current network status (optional utility component)
 */
export const NetworkStatus = ({ className = '' }) => {
  const networkInfo = useNetwork();
  
  const statusConfig = {
    high: { color: '#4D9078', icon: '📶', label: 'Fast Connection' },
    medium: { color: '#C9A96E', icon: '📶', label: 'Moderate Connection' },
    low: { color: '#B85C38', icon: '📶', label: 'Slow Connection' }
  };
  
  const config = statusConfig[networkInfo.quality];
  
  if (!networkInfo.isOnline) {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${className}`}
           style={{ backgroundColor: '#B85C38', color: '#FFFFFF' }}>
        <span>📵</span>
        <span>Offline</span>
      </div>
    );
  }
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${className}`}
         style={{ backgroundColor: config.color + '20', color: config.color }}>
      <span>{config.icon}</span>
      <span>{config.label}</span>
      {networkInfo.saveData && <span className="text-xs">(Data Saver)</span>}
    </div>
  );
};

export default LazyImage;
